import { Component, OnInit } from '@angular/core';
import { CustomFieldConstants } from '../../models/custom-field.interface';
import { AgilityRiskService } from '../../providers/agility-risk.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AgilityRiskSpinnerService } from '../../providers/agility-risk-spinner.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationModalComponent } from '../shared/modals/confirmation-modal/confirmation-modal.component';
import * as _ from 'lodash';
import { AgilityRiskMessageService } from '../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-custom-fields-management',
  templateUrl: './custom-fields-management.component.html',
  styleUrls: ['./custom-fields-management.component.scss']
})
export class CustomFieldsManagementComponent implements OnInit {

  customFieldSelected: any = {};
  submitted: boolean;

  areaId: string;
  customFieldsList: any[];
  customFieldDataTypes: { label: string; value: string }[];

  constructor(
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private riskService: AgilityRiskService,
    private translateService: TranslateService,
    private messageService: AgilityRiskMessageService,
    private spinner: AgilityRiskSpinnerService,
    private dialogService: DialogService) {
    this.areaId = this.activatedRoute.snapshot?.params?.areaId;
  }

  ngOnInit(): void {
    this.setCustomFieldsDataTypes();
    this.getCustomFieldsList();
  }

  setCustomFieldsDataTypes() {
    this.customFieldDataTypes = Object.keys(CustomFieldConstants)
      .filter(cfcKey => [CustomFieldConstants.customField,
        CustomFieldConstants.nativeField,
        CustomFieldConstants.associationField,
        CustomFieldConstants.checkbox].indexOf(CustomFieldConstants[cfcKey]) < 0)
      .map(cfcKey => {
        return {
          label: this.translateService.instant(`customFields.customFieldsDataTypes.${CustomFieldConstants[cfcKey]}`),
          value: CustomFieldConstants[cfcKey].toUpperCase()
        };
      });
  }

  getCustomFieldsList() {
    this.submitted = false;
    this.spinner.start();
    this.riskService.getCustomFields(this.areaId).subscribe({
      next: value => {
        this.onCustomFieldsLoaded(value);
        this.spinner.stop();
      },
      error: err => {
        this.messageService.error(this.translateService.instant('customFields.msgFailedToLoadCustomFields'));
        this.spinner.stop();
      }
    });
  }

  onCustomFieldsLoaded(customFields: any[]) {
    this.customFieldsList = customFields;
    this.customFieldsList.forEach(v => {
      v.typeDisplay = v.type ? this.translateService.instant(`customFields.customFieldsDataTypes.${v.type.toLowerCase()}`) : '';
    });
    this.initNewCustomField();
  }

  onSaveCustomField() {
    this.submitted = true;
    if (!this.isValid) {
      this.messageService.error(this.translateService.instant('common.pleaseFillAllRequiredFields'));
      return;
    }

    this.spinner.start();
    this.riskService.saveCustomField(this.customFieldSelected).subscribe({
      next: res => {
        this.messageService.success(this.translateService.instant('customFields.msgSuccessfullySavedCustomField'));
        this.getCustomFieldsList();
        this.initNewCustomField();
        this.spinner.stop();
      },
      error: err => {
        this.messageService.error(this.translateService.instant('customFields.msgFailedToSavedCustomField'));
        this.spinner.stop();
      }
    });
  }

  get isValid(): boolean {
    if (!this.customFieldSelected?.name) {
      return false;
    }

    return true;
  }

  initNewCustomField() {
    this.customFieldSelected = {};
    this.customFieldSelected.areaType = this.areaId;
    this.customFieldSelected.position = this.customFieldsList.length;
    this.customFieldSelected.type = CustomFieldConstants.text.toUpperCase();
    this.customFieldSelected.required = false;
    this.customFieldSelected.hidden = false;
  }

  editCustomField(customField: any) {
    this.customFieldSelected = _.cloneDeep(customField);
  }

  onDeleteCustomField(customField: any) {
    const dialogRef = this.dialogService.open(ConfirmationModalComponent, {
      data: {
        body: this.translateService.instant('customFields.deleteCustomFieldBody')
      },
      header: this.translateService.instant('customFields.deleteCustomFieldHeader'),
      width: '35%'
    });

    dialogRef.onClose.subscribe(
      res => {
        if (res) {
          this.deleteCustomField(customField.id);
        }
      }
    );
  }

  deleteCustomField(customFieldId: string) {
    this.spinner.start();
    this.riskService.deleteCustomField(customFieldId).subscribe({
      next: res => {
        this.getCustomFieldsList();
        this.messageService.success(this.translateService.instant('common.deleted'));
        this.spinner.stop();
      },
      error: err => {
        this.messageService.success(this.translateService.instant('common.failedToDeleted'));
        this.spinner.stop();
      }
    });
  }

  onCustomFieldsReorder(dragIndex: number, dropIndex: number) {
    const newOrder = this.customFieldsList.map((cf, index) => {
      cf.position = index;
      return {
        order: index,
        id: cf.id
      };
    });
    this.spinner.start();
    this.riskService.reorderCustomFields(this.areaId, newOrder).subscribe({
      next: res => {
        this.onCustomFieldsLoaded(res);
        this.messageService.success(this.translateService.instant('common.saved'));
        this.spinner.stop();
      },
      error: err => {
        this.messageService.error(this.translateService.instant('common.failedToSave'));
        this.spinner.stop();
      }
    });
  }

  onDoneWithTheFields() {
    this.route.navigate(['dashboard'], {relativeTo: this.activatedRoute.parent});
  }
}
